import React, { useEffect, useState } from "react";
import { Trophy, Star, Wrench, Loader2, Briefcase, CheckCircle2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";

const Stars = ({ value }) => {
  if (value == null) return <span className="text-[#9aa6ad] text-xs">—</span>;
  const full = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={12}
          className={n <= full ? "fill-[#f28a16] text-[#f28a16]" : "text-[#cfd7db]"}
        />
      ))}
      <span className="ml-1 text-xs font-semibold text-[#3f4b52]">{value.toFixed(1)}</span>
    </span>
  );
};

export const AdminPerformance = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setRows(await adminApi.fitterPerformance());
      } catch {
        toast.error("Could not load performance");
      } finally { setLoading(false); }
    })();
  }, []);

  const totals = rows.reduce(
    (a, r) => ({
      total_jobs: a.total_jobs + r.total_jobs,
      completed: a.completed + r.completed_jobs,
      revenue: a.revenue + r.revenue_inc_vat,
    }),
    { total_jobs: 0, completed: 0, revenue: 0 }
  );

  return (
    <div data-testid="admin-performance-page">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Fitter Performance</h1>
      <p className="text-[#3f4b52] mt-1">Per-fitter job throughput, revenue and customer ratings.</p>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Summary icon={Wrench} label="Fitters" value={rows.length} color="#20282c" />
        <Summary icon={Briefcase} label="Total jobs" value={totals.total_jobs} color="#3b82f6" />
        <Summary icon={CheckCircle2} label="Completed" value={totals.completed} color="#10b981" />
        <Summary icon={Wallet} label="Revenue (incl. VAT)" value={money(totals.revenue)} color="#f28a16" />
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#3f4b52] text-sm">
            No fitter data yet — assign a "Fitter / Team leader" on jobs to see performance.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left px-4 py-2.5">Fitter</th>
                  <th className="text-right px-4 py-2.5">Jobs</th>
                  <th className="text-right px-4 py-2.5">Completed</th>
                  <th className="text-right px-4 py-2.5">Paid</th>
                  <th className="text-right px-4 py-2.5">Revenue (incl. VAT)</th>
                  <th className="text-left px-4 py-2.5">Customer service</th>
                  <th className="text-left px-4 py-2.5">Premises</th>
                  <th className="text-left px-4 py-2.5">Quality of work</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {rows.map((r, i) => (
                  <tr key={r.fitter} data-testid={`fitter-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {i === 0 && rows.length > 1 && <Trophy size={14} className="text-[#f28a16]" />}
                        <span className="font-display font-bold text-[#111619]">{r.fitter}</span>
                      </div>
                      {r.ratings_count > 0 && (
                        <div className="text-xs text-[#3f4b52] mt-0.5">
                          {r.ratings_count} rating{r.ratings_count === 1 ? "" : "s"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-[#111619] font-semibold">{r.total_jobs}</td>
                    <td className="px-4 py-3 text-right text-[#10b981] font-semibold">{r.completed_jobs}</td>
                    <td className="px-4 py-3 text-right text-[#3b82f6] font-semibold">{r.paid_jobs}</td>
                    <td className="px-4 py-3 text-right text-[#111619] font-semibold">{money(r.revenue_inc_vat)}</td>
                    <td className="px-4 py-3"><Stars value={r.avg_customer_service} /></td>
                    <td className="px-4 py-3"><Stars value={r.avg_premises} /></td>
                    <td className="px-4 py-3"><Stars value={r.avg_quality_of_work} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Summary = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md p-4">
    <div className="flex items-center justify-between mb-1.5">
      <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">{label}</div>
      <div className="h-8 w-8 rounded-md inline-flex items-center justify-center" style={{ background: `${color}1a`, color }}>
        <Icon size={16} />
      </div>
    </div>
    <div className="font-display font-black text-2xl text-[#111619]">{value}</div>
  </div>
);

export default AdminPerformance;
